<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, ref } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import BankLogo from './BankLogo.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  banks: { type: Array, default: () => [] },
  busy: { type: Boolean, default: false },
});
defineEmits(['configure', 'toggle']);
const search = ref('');
const filter = ref('all');
const filters = [
  { value: 'all', label: 'Todos' },
  { value: 'configured', label: 'Configurados' },
  { value: 'active', label: 'Ativas' },
  { value: 'inactive', label: 'Inativas' },
  { value: 'beta', label: 'Beta' },
  { value: 'available', label: 'Para conectar' },
];
const visibleBanks = computed(() =>
  props.banks.filter(bank => {
    if (filter.value === 'active' && !bank.enabled) return false;
    if (filter.value === 'inactive' && (!bank.configured || bank.enabled))
      return false;
    if (filter.value === 'beta' && !bank.beta) return false;
    if (filter.value === 'configured' && !bank.configured) return false;
    if (filter.value === 'available' && bank.configured) return false;
    const normalize = text =>
      text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    return normalize(
      `${bank.title} ${bank.description} ${bank.products}`
    ).includes(normalize(search.value.trim()));
  })
);
</script>

<template>
  <section class="space-y-4">
    <div
      class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <h4 class="text-[13px] font-semibold text-n-slate-12">
          Escolha uma integração
        </h4>
        <p class="mt-1 text-xs text-n-slate-11">
          Gerencie seus bancos ou configure uma nova conexão.
        </p>
      </div>
      <Input
        v-model="search"
        type="search"
        placeholder="Buscar banco ou produto"
        class="w-full sm:w-72"
      />
    </div>
    <div class="flex flex-wrap items-center gap-2">
      <Button
        v-for="item in filters"
        :key="item.value"
        :label="item.label"
        size="sm"
        :color="filter === item.value ? 'teal' : 'slate'"
        :variant="filter === item.value ? 'faded' : 'ghost'"
        :aria-pressed="filter === item.value"
        @click="filter = item.value"
      />
      <span class="ml-auto text-xs text-n-slate-10">
        {{ visibleBanks.length }} integrações
      </span>
    </div>
    <div
      class="grid grid-cols-1 gap-3 pt-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
    >
      <article
        v-for="bank in visibleBanks"
        :key="bank.value"
        class="relative flex min-w-0 flex-col gap-2 rounded-lg border bg-n-solid-1 p-2.5 transition-colors"
        :class="[
          bank.enabled ? 'border-n-teal-7' : 'border-n-strong',
          busy
            ? 'cursor-wait'
            : 'cursor-pointer hover:bg-n-alpha-1 hover:border-n-brand',
        ]"
      >
        <span
          v-if="bank.isNew"
          class="pointer-events-none absolute -top-2 right-2 z-10 rounded-full border border-n-brand/20 bg-n-solid-1 px-2 py-0.5 text-[10px] font-semibold leading-3 text-n-brand"
          >Novo</span
        >
        <button
          type="button"
          class="absolute inset-0 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
          :class="busy ? 'cursor-wait' : 'cursor-pointer'"
          :aria-label="`${bank.configured ? 'Configurar' : 'Conectar'} ${bank.title}`"
          :disabled="busy"
          @click="$emit('configure', bank.value)"
        />
        <div class="flex items-center gap-2">
          <BankLogo :provider="bank.value" />
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <h5 class="text-[13px] font-semibold text-n-slate-12">
                {{ bank.title }}
              </h5>
              <span
                v-if="bank.beta"
                class="inline-flex items-center gap-1 rounded bg-n-alpha-2 px-1.5 py-0.5 text-[10px] font-medium text-n-slate-11"
              >
                <span class="i-lucide-flask-conical size-3" />
                Beta
              </span>
            </div>
            <p class="m-0 line-clamp-2 text-[11px] leading-4 text-n-slate-11">
              {{ bank.description }}
            </p>
          </div>
        </div>
        <div
          class="flex flex-wrap items-center justify-between gap-2 text-[11px]"
        >
          <span
            class="inline-flex items-center gap-1 text-[11px]"
            :class="
              bank.enabled
                ? 'rounded-md bg-n-teal-3 px-2 py-1 font-semibold text-n-teal-11'
                : bank.configured
                  ? 'py-1 text-n-slate-11'
                  : 'py-1 text-n-slate-10'
            "
          >
            <span
              class="size-3"
              :class="
                bank.enabled
                  ? 'i-lucide-circle-check'
                  : bank.configured
                    ? 'i-lucide-circle-pause'
                    : 'i-lucide-plug'
              "
            />
            {{
              bank.enabled
                ? 'Ativa'
                : bank.configured
                  ? 'Inativa'
                  : 'Sem configuração'
            }}
          </span>
          <span v-if="bank.products" class="text-n-slate-11">
            {{ bank.products }}
          </span>
        </div>
        <div
          class="mt-auto flex min-h-7 flex-wrap items-center justify-end gap-1.5 border-t border-n-weak pt-1.5"
        >
          <label
            v-if="bank.configured"
            :for="`bank-status-${bank.value}`"
            class="relative z-10 inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-n-slate-11"
            :class="{ 'cursor-not-allowed opacity-50': busy }"
          >
            <Switch
              :id="`bank-status-${bank.value}`"
              :model-value="bank.enabled"
              :aria-label="`${bank.enabled ? 'Desativar' : 'Ativar'} ${bank.title}`"
              :disabled="busy"
              @update:model-value="$emit('toggle', bank.value)"
            />
            <span>{{ bank.enabled ? 'Desativar' : 'Ativar' }}</span>
          </label>
        </div>
      </article>
    </div>
    <p
      v-if="!visibleBanks.length"
      class="rounded-xl border border-dashed border-n-strong p-8 text-center text-sm text-n-slate-11"
    >
      Nenhuma integração encontrada para estes filtros.
    </p>
  </section>
</template>
